import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import Modal from './Common/Modal';
import { useAuth } from '../contexts/AuthContext';

export default function TeacherManagement() {
  const { user: currentUser } = useAuth();
  const role = currentUser?.role;
  const canEdit = ['admin', 'superintendent', 'principal'].includes(role);

  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [assignModal, setAssignModal] = useState(null);
  const [search, setSearch] = useState('');
  const [filterCampus, setFilterCampus] = useState('all');   // 👈 NEW
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', password: '',
    phone: '', address: '', cnic: '',
  });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [t, c, camp] = await Promise.all([
        api.get('/users/?role=teacher'),
        api.get('/classes/'),
        api.get('/campuses/').catch(() => ({ data: [] })),
      ]);
      setTeachers(t.data || []);
      setClasses(c.data || []);
      setCampuses(camp.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getClassForTeacher = (teacherId) =>
    classes.find(c => c.teacher_id === teacherId);

  const getCampusName = (campusId) => {
    const c = campuses.find(x => x.id === campusId);
    return c ? c.name : '—';
  };

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    setError('');

    if (!form.first_name || !form.last_name) {
      setError('First and last name are required.');
      return;
    }
    if (!editing && (!form.email || form.password.length < 8)) {
      setError('Email is required and password must be at least 8 characters.');
      return;
    }

    setBusy(true);
    try {
      if (editing) {
        await api.put(`/users/${editing.id}`, {
          first_name: form.first_name,
          last_name: form.last_name,
          phone: form.phone,
          address: form.address,
        });
      } else {
        await api.post('/users/', {
          email: form.email,
          password: form.password,
          first_name: form.first_name,
          last_name: form.last_name,
          phone: form.phone || '',
          address: form.address || '',
          cnic: form.cnic || null,
          role: 'teacher',
        });
      }
      setModalOpen(false);
      resetForm();
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleAssignClass = async (classId) => {
    if (!assignModal) return;
    setBusy(true);
    try {
      const currentClass = getClassForTeacher(assignModal.id);

      if (currentClass && currentClass.id !== parseInt(classId)) {
        await api.put(`/classes/${currentClass.id}`, {
          campus_id: currentClass.campus_id,
          name: currentClass.name,
          section: currentClass.section,
          teacher_id: null,
          academic_year: currentClass.academic_year,
        });
      }

      if (classId) {
        const target = classes.find(c => c.id === parseInt(classId));
        await api.put(`/classes/${classId}`, {
          campus_id: target.campus_id,
          name: target.name,
          section: target.section,
          teacher_id: assignModal.id,
          academic_year: target.academic_year,
        });
      }

      setAssignModal(null);
      fetchAll();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setBusy(false);
    }
  };

  const resetForm = () => {
    setForm({ first_name: '', last_name: '', email: '', password: '',
      phone: '', address: '', cnic: '' });
    setEditing(null);
    setError('');
  };

  const openEdit = (teacher) => {
    setEditing(teacher);
    setForm({
      first_name: teacher.first_name,
      last_name: teacher.last_name,
      email: teacher.email,
      password: '',
      phone: teacher.phone || '',
      address: teacher.address || '',
      cnic: teacher.cnic || '',
    });
    setModalOpen(true);
  };

  // ─── ✅ Apply search + campus filters ───
  const filtered = teachers.filter(t => {
    // Campus filter — teacher's campus = their class's campus, or fallback to their own campus_id
    if (filterCampus !== 'all') {
      const cls = getClassForTeacher(t.id);
      const teacherCampusId = cls ? cls.campus_id : t.campus_id;
      if (teacherCampusId !== parseInt(filterCampus)) return false;
    }

    // Search
    if (search) {
      const q = search.toLowerCase();
      const blob = `${t.first_name} ${t.last_name} ${t.email} ${t.cnic || ''} ${t.phone || ''}`.toLowerCase();
      if (!blob.includes(q)) return false;
    }

    return true;
  });

  // ─── Campus counts (clickable filter cards) ───
  const campusCounts = campuses.map(c => ({
    ...c,
    count: teachers.filter(t => {
      const cls = getClassForTeacher(t.id);
      const teacherCampusId = cls ? cls.campus_id : t.campus_id;
      return teacherCampusId === c.id;
    }).length,
  }));

  const unassignedCount = teachers.filter(t => !getClassForTeacher(t.id)).length;

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <h2 className="text-xl font-bold">👨‍🏫 Teacher Management</h2>
        <div className="flex gap-2">
          <button onClick={fetchAll} disabled={loading}
            className="bg-blue-100 text-blue-700 px-3 py-2 rounded hover:bg-blue-200 text-sm">
            <i className="fas fa-sync-alt mr-1"></i> Refresh
          </button>
          {canEdit && (
            <button onClick={() => { resetForm(); setModalOpen(true); }}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm">
              <i className="fas fa-plus mr-1"></i> Add Teacher
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-white p-4 rounded shadow border-l-4 border-blue-500">
          <p className="text-xs text-gray-500">Total Teachers</p>
          <p className="text-2xl font-bold text-blue-700">{teachers.length}</p>
        </div>
        <div className="bg-white p-4 rounded shadow border-l-4 border-green-500">
          <p className="text-xs text-gray-500">Assigned Class</p>
          <p className="text-2xl font-bold text-green-700">{teachers.length - unassignedCount}</p>
        </div>
        <div className="bg-white p-4 rounded shadow border-l-4 border-yellow-500">
          <p className="text-xs text-gray-500">Unassigned</p>
          <p className="text-2xl font-bold text-yellow-700">{unassignedCount}</p>
        </div>
      </div>

      {/* ─── ✅ Campus filter cards (clickable) ─── */}
      <div className="grid grid-cols-3 md:grid-cols-4 gap-3 mb-4">
        <button
          type="button"
          onClick={() => setFilterCampus('all')}
          className={`text-left bg-white p-3 rounded shadow border-l-4 transition-all ${
            filterCampus === 'all'
              ? 'border-gray-700 ring-2 ring-gray-300'
              : 'border-gray-300 hover:border-gray-500'
          }`}
        >
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <i className="fas fa-users text-gray-700"></i> All Campuses
          </p>
          <p className="text-2xl font-bold text-gray-800">{teachers.length}</p>
        </button>

        {campusCounts.map(c => {
          const isActive = filterCampus === String(c.id);
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => setFilterCampus(prev => prev === String(c.id) ? 'all' : String(c.id))}
              className={`text-left bg-white p-3 rounded shadow border-l-4 transition-all ${
                isActive
                  ? 'border-blue-500 ring-2 ring-offset-1 ring-gray-300 bg-blue-50'
                  : 'border-blue-200 hover:border-blue-500 hover:shadow-md'
              }`}
            >
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <i className="fas fa-school text-blue-600"></i> {c.name}
              </p>
              <p className="text-2xl font-bold text-blue-700">{c.count}</p>
            </button>
          );
        })}
      </div>

      {/* ─── Search + campus dropdown ─── */}
      <div className="bg-white p-3 rounded shadow mb-4 flex flex-wrap gap-3 items-center">
        <select className="p-2 border rounded text-sm"
          value={filterCampus}
          onChange={e => setFilterCampus(e.target.value)}>
          <option value="all">All Campuses</option>
          {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        <input type="text" placeholder="Search by name, email, phone, CNIC..."
          className="flex-1 min-w-[200px] p-2 border rounded text-sm"
          value={search} onChange={e => setSearch(e.target.value)} />

        <span className="ml-auto text-sm text-gray-500">
          Showing {filtered.length} of {teachers.length}
        </span>
      </div>

      <div className="bg-white p-4 rounded shadow">
        {loading ? <p className="text-gray-500">Loading…</p> : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <i className="fas fa-chalkboard-teacher text-4xl mb-3"></i>
            <p>No teachers found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Name', 'Email', 'Phone', 'CNIC', 'Campus', 'Assigned Class', 'Status', 'Actions'].map(h => (
                    <th key={h} className="p-3 text-left font-semibold text-gray-700">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(t => {
                  const cls = getClassForTeacher(t.id);
                  const campusId = cls ? cls.campus_id : t.campus_id;
                  return (
                    <tr key={t.id} className="border-t hover:bg-gray-50">
                      <td className="p-3 font-medium">{t.first_name} {t.last_name}</td>
                      <td className="p-3 text-gray-600">{t.email}</td>
                      <td className="p-3">{t.phone || '—'}</td>
                      <td className="p-3 font-mono text-xs">{t.cnic || '—'}</td>
                      <td className="p-3">{getCampusName(campusId)}</td>
                      <td className="p-3">
                        {cls ? (
                          <span className="px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-800">
                            {cls.name}
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded text-xs bg-yellow-100 text-yellow-800">
                            Not Assigned
                          </span>
                        )}
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded text-xs ${t.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {t.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          {canEdit && (
                            <>
                              <button onClick={() => setAssignModal(t)}
                                className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded hover:bg-purple-200"
                                title="Assign class">
                                <i className="fas fa-link"></i>
                              </button>
                              <button onClick={() => openEdit(t)}
                                className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
                                title="Edit">
                                <i className="fas fa-edit"></i>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); resetForm(); }}
        title={editing ? 'Edit Teacher' : 'Add Teacher'}
        onSave={handleSubmit}
        saveLabel={editing ? 'Update' : 'Create'}
        size="lg"
      >
        <div className="space-y-3">
          {error && <div className="bg-red-50 text-red-600 p-2 rounded text-sm">{error}</div>}

          <div className="grid grid-cols-2 gap-3">
            <input className="p-2 border rounded" placeholder="First Name *"
              value={form.first_name}
              onChange={e => setForm({...form, first_name: e.target.value})} />
            <input className="p-2 border rounded" placeholder="Last Name *"
              value={form.last_name}
              onChange={e => setForm({...form, last_name: e.target.value})} />
          </div>

          <input className="w-full p-2 border rounded" type="email" placeholder="Email *"
            value={form.email} disabled={!!editing}
            onChange={e => setForm({...form, email: e.target.value})} />

          {!editing && (
            <input className="w-full p-2 border rounded" type="password"
              placeholder="Password (min 8 chars) *"
              value={form.password}
              onChange={e => setForm({...form, password: e.target.value})} />
          )}

          <div className="grid grid-cols-2 gap-3">
            <input className="p-2 border rounded" placeholder="Phone"
              value={form.phone}
              onChange={e => setForm({...form, phone: e.target.value})} />
            <input className="p-2 border rounded" placeholder="CNIC"
              value={form.cnic}
              onChange={e => setForm({...form, cnic: e.target.value})} />
          </div>

          <input className="w-full p-2 border rounded" placeholder="Address"
            value={form.address}
            onChange={e => setForm({...form, address: e.target.value})} />

          <p className="text-xs text-gray-500 italic">
            💡 After creating, click the 🔗 icon in the table to assign a class teacher.
          </p>
        </div>
      </Modal>

      {/* Assign Class Modal */}
      <Modal
        isOpen={!!assignModal}
        onClose={() => setAssignModal(null)}
        title={`Assign Class — ${assignModal?.first_name || ''} ${assignModal?.last_name || ''}`}
        showFooter={false}
      >
        {assignModal && (
          <div className="space-y-3">
            <p className="text-sm text-gray-500">
              Select a class to make <strong>{assignModal.first_name} {assignModal.last_name}</strong> the class teacher.
            </p>

            {(() => {
              const current = getClassForTeacher(assignModal.id);
              if (current) {
                return (
                  <div className="bg-green-50 border border-green-200 p-3 rounded flex justify-between items-center">
                    <p className="text-sm text-green-800">
                      <strong>Currently:</strong> Class {current.name}
                    </p>
                    <button onClick={() => handleAssignClass('')} disabled={busy}
                      className="text-red-600 hover:underline text-xs font-medium">
                      Unassign
                    </button>
                  </div>
                );
              }
              return null;
            })()}

            <div className="max-h-96 overflow-y-auto space-y-2">
              {classes.filter(c => !c.teacher_id || c.teacher_id === assignModal.id).map(c => {
                const isCurrent = c.teacher_id === assignModal.id;
                return (
                  <button key={c.id}
                    onClick={() => !isCurrent && handleAssignClass(c.id)}
                    disabled={isCurrent || busy}
                    className={`w-full p-3 rounded border text-left transition ${
                      isCurrent
                        ? 'bg-green-50 border-green-300 cursor-not-allowed'
                        : 'bg-white border-gray-200 hover:border-blue-400 hover:bg-blue-50'
                    }`}>
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-semibold">{c.name}</p>
                        <p className="text-xs text-gray-500">
                          {getCampusName(c.campus_id)} · Section {c.section || '—'}
                        </p>
                      </div>
                      {isCurrent ? (
                        <span className="text-xs text-green-600 font-semibold">Current ✓</span>
                      ) : (
                        <span className="text-xs text-blue-600">Assign →</span>
                      )}
                    </div>
                  </button>
                );
              })}
              {classes.filter(c => !c.teacher_id || c.teacher_id === assignModal.id).length === 0 && (
                <p className="text-center text-gray-400 py-8">
                  All classes already have a teacher assigned.
                </p>
              )}
            </div>

            <div className="flex justify-end pt-2 border-t">
              <button onClick={() => setAssignModal(null)}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}